<template>
	<div>
		<section class="b360-grid-row">
			<div class="b360-col-4 btn-fixed-bottom">
				<button
					class="b360-btn b360-btn--workspace-primary"
					type="button"
					@click="navigateBack"
				>
					&#60; Go Back
				</button>
			</div>
		</section>

		<section class="tab-container">
			<button
				class="b360-btn b360-btn--workspace-primary tab"
				:class="{ 'tab-inactive': tabSelected !== 0 }"
				@click="tabSelected = 0"
				type="button"
			>
				Eligible Products
			</button>
			<button
				class="b360-btn b360-btn--workspace-primary tab"
				:class="{ 'tab-inactive': tabSelected !== 1 }"
				@click="tabSelected = 1"
				type="button"
			>
				Ineligible Products
			</button>
		</section>

		<form
			id="lendwize-product-form"
			class="lendwize-product-form"
			@submit.prevent=""
		>
			<template v-for="(category, catIndex) in productData">
				<section
					v-if="tabSelected === 0"
					class="scenarios-container"
					:class="{ 'limit-records': category.products.length <= 3 }"
					:key="category.categoryId"
				>
					<div class="scenarios-description" v-if="catIndex === 0">
						Your eligible products are listed below. Please select up to three
						scenarios to display on your flyer.
					</div>

					<div
						class="scenarios-header"
						:class="{ closed: !category.show }"
						@click="toggleEligible({ catIndex: catIndex })"
					>
						{{ category.categoryName }}

						<font-awesome-icon
							class="accordion-toggle"
							:class="{ open: category.show }"
							:icon="['fal', 'chevron-circle-down']"
						/>
					</div>

					<div
						class="b360-grid-row scenarios-row scenarios-row-header"
						v-if="category.show"
					>
						<div class="b360-col-1 scenarios-col empty"></div>
						<div
							:class="{
								'b360-col-4': !canViewFinalPricing,
								'b360-col-2': canViewFinalPricing,
							}"
							class="scenarios-col"
						>
							Interest
						</div>
						<div class="b360-col-4 scenarios-col">Monthly Payment</div>
						<div v-if="canViewFinalPricing" class="b360-col-5 scenarios-col">
							Final Price
						</div>
					</div>

					<template v-for="(product, index) in category.products">
						<div
							v-if="index < category.optionsDisplayLimit && category.show"
							class="b360-grid-row scenarios-row"
							:class="{ selected: product.selected }"
							@click.stop="
								handleProductSelection(catIndex, index, product.selected)
							"
							:key="index"
							data-test="eligible-product"
						>
							<div class="b360-col-1 product-checkbox">
								<CheckBox
									:default="false"
									:checkboxId="`${category.categoryNameRef}-${index}`"
									:checkboxDescription="`${category.categoryName} - ${product.interest}, ${product.monthlyPayment}, ${product.finalPrice}`"
									:isChecked="product.selected"
								/>
							</div>
							<div
								:class="{
									'b360-col-4': !canViewFinalPricing,
									'b360-col-2': canViewFinalPricing,
								}"
								class="b360-col-2"
							>
								{{ product.interest }}%
							</div>
							<div class="b360-col-4">${{ product.monthlyPayment }}</div>
							<div v-if="canViewFinalPricing" class="b360-col-5">
								{{ product.finalPrice }}
							</div>
						</div>
					</template>

					<div
						class="b360-grid-row scenarios-row scenarios-adjust-options"
						v-if="category.products.length > 3 && category.show"
					>
						<div class="b360-col-12">
							<button
								@click="adjustOptionsLimit(catIndex)"
								data-test="options-limit-button"
							>
								{{
									category.optionsDisplayLimit === 3
										? 'More Options'
										: 'Fewer Options'
								}}
							</button>
						</div>
					</div>
				</section>
			</template>
			<template v-for="(product, index) in ineligibleData">
				<section
					v-if="tabSelected === 1"
					class="scenarios-container"
					:key="product.id"
				>
					<div class="scenarios-description" v-if="index === 0">
						Your ineligible products are listed below. Click to display the
						reason for ineligibility.
					</div>

					<div
						class="scenarios-header"
						:class="{ closed: !product.show }"
						@click="toggleIneligible({ index: index })"
					>
						{{ product.name }}

						<font-awesome-icon
							class="accordion-toggle"
							:class="{ open: product.show }"
							:icon="['fal', 'chevron-circle-down']"
						/>
					</div>

					<div v-if="product.reasons && product.show" class="b360-grid-row">
						<div class="b360-col-12 ineligible-row">
							<p class="ineligible-text reasons-header">Reasons:</p>
							<template v-for="(reason, index) in product.reasons">
								<p class="ineligible-text" :key="index">
									{{ index + 1 }}: {{ reason }}
								</p>
							</template>
						</div>
					</div>
				</section>
			</template>
		</form>
		<button class="b360-btn b360-btn--workspace-primary" @click="nextStep()">
			Next Step
		</button>
		<BaseModal
			v-if="showMessageModal"
			modalTitle="Eligible Products"
			:showClose="true"
			@close="showMessageModal = false"
			class="message-modal"
		>
			<div class="modal-body" slot="modalBody">
				This flyer can include a maximum of three scenarios. You must deselect a
				scenario to make this selection.
			</div>
			<div slot="modalFooterButtons">
				<BaseButton
					color="blue"
					class="action-button"
					@onClick="showMessageModal = false"
					>OK
				</BaseButton>
			</div>
		</BaseModal>
	</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import BaseButton from '@/components/global/BaseButton.vue';
import BaseModal from '@/components/global/BaseModal.vue';
import CheckBox from '@/components/global/CheckBox.vue';
import productPricingClient from '@/services/toolbox/product-and-pricing-service';

export default {
	name: 'BbPricingProducts',

	components: {
		BaseButton,
		BaseModal,
		CheckBox,
	},

	data() {
		return {
			tabSelected: 0,
			canViewFinalPricing: false,
			showMessageModal: false,
		};
	},

	computed: {
		...mapState({
			productData: (state) => state.lendwizePricingScenario.productData,
			ineligibleData: (state) => state.lendwizePricingScenario.ineligibleData,
			entityId: (state) => state.companyProfile.rolodexEntityId,
		}),
		disclaimer: {
			get() {
				return this.$store.state.lendwizePricingScenario.flyerDisclaimer;
			},
			set(value) {
				this.$store.commit('SET_DISCLAIMER', value);
			},
		},
	},

	mounted() {
		// scroll to top
		const bbNestedView = document.querySelector('#bb-nested-view');
		if (bbNestedView !== null) bbNestedView.scrollTop = 0;

		if (this.productData.length === 0) {
			this.tabSelected = 1;
		}
		productPricingClient.canSeePricing(this.entityId).then((response) => {
			this.canViewFinalPricing = response.data;
		});
	},

	methods: {
		...mapActions([
			'updatePricingFields',
			'adjustOptionsLimitState',
			'toggleProduct',
			'updateFlyerData',
			'toggleEligible',
			'toggleIneligible',
		]),

		navigateBack() {
			this.updatePricingFields({
				fieldName: 'activeForm',
				value: 1,
			});
		},

		adjustOptionsLimit(index) {
			const prodRef = this.productData[index];
			const currentState = prodRef.optionsDisplayLimit;
			let numProductsBuried = 0;

			if (prodRef.products.length > 3) {
				for (let i = 3; i < prodRef.products.length; i++) {
					if (prodRef.products[i].selected) {
						numProductsBuried += 1;
					}
				}
			}

			// if collapsing, make sure no selections will be buried
			if (currentState === 3 || numProductsBuried === 0) {
				this.adjustOptionsLimitState(index);
			}
		},

		handleProductSelection(catIndex, prodIndex, currentlySelected) {
			const numProductsSelected = [...this.productData.map((e) => e.products)]
				.flat()
				.filter((e) => e.selected).length;

			if (currentlySelected || numProductsSelected < 3) {
				this.toggleProduct({
					catIndex,
					prodIndex,
				});
				const productsSelected = [...this.productData.map((e) => e.products)]
					.flat()
					.filter((e) => e.selected);
				this.addFinanceChargeToDisclaimer(productsSelected);
				this.updateFlyerData();
			} else {
				this.showMessageModal = true;
			}
		},

		addFinanceChargeToDisclaimer(selectedProducts) {
			const disclaimerText = [
				'* APR is based on estimated finance charges of  $ {financeCharge}.',
				'** APR is based on estimated finance charges of $ {financeCharge}.',
				'*** APR is based on estimated finance charges of $ {financeCharge}.',
			];
			this.disclaimer = '';

			// TODO: refactor to eliminate linting error
			selectedProducts.forEach((el, index) => {
				this.disclaimer +=
					// eslint-disable-next-line prefer-template
					' ' +
					(disclaimerText[index] || '').replace(
						'{financeCharge}',
						el.financeCharges,
					);
			});
		},

		nextStep() {
			this.$router.push({
				path: `/campaign-editor-workspace/${this.$route.params.campaignId}/upload-images`,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.tab-container {
	@include rem(margin-bottom, 20px);

	.tab {
		width: 50%;

		&.tab-inactive {
			background: $b360-color--white;
			color: $brand-teal;
			border: 1px solid $brand-teal;
		}

		&:first-of-type {
			border-top-right-radius: 0;
			border-bottom-right-radius: 0;
		}

		&:last-of-type {
			border-top-left-radius: 0;
			border-bottom-left-radius: 0;
		}
	}
}

.ineligible-row {
	box-sizing: border-box;
	@include rem(padding, 16px 16px 4px 16px);
	border: 1px solid $b360-color--gray__light;
	border-top: none;
	border-bottom-left-radius: 0.3125rem;
	border-bottom-right-radius: 0.3125rem;
	.ineligible-text {
		margin: unset;
		padding: unset;
		@include rem(padding-top, 12px);

		&.reasons-header {
			font-weight: bold;
			@include rem(padding, 0 0 2px 0);
		}
	}
}

.scenarios-form-label {
	@include rem(margin-top, 10px);
}

.scenarios-container {
	margin-bottom: 1rem;
	font-size: 14px;

	.scenarios-description {
		@include rem(margin-bottom, 16px);
		font-size: 15px;
	}

	.scenarios-header {
		box-sizing: border-box;
		border: 1px solid $b360-color--gray__light;
		border-top-left-radius: 0.3125rem;
		border-top-right-radius: 0.3125rem;
		@include rem(padding, 16px 16px 17px 16px);
		@include rem(height, 54px);
		background-color: $off-white;
		color: $b360-color--gray__darkest;
		font-weight: bold;
		font-size: 15px;
		overflow: hidden;
		white-space: nowrap;
		cursor: pointer;
		user-select: none;

		&.closed {
			border-bottom-left-radius: 0.3125rem;
			border-bottom-right-radius: 0.3125rem;
		}
	}

	.scenarios-row {
		margin-bottom: 0;
		box-sizing: border-box;
		@include rem(padding, 10px);
		@include rem(height, 41px);
		border: 1px solid $b360-color--gray__light;
		border-top: none;
		font-weight: 300;
		user-select: none;
		cursor: pointer;
		&.scenarios-row-header {
			font-weight: 500;

			.scenarios-col {
				white-space: nowrap;

				&.empty {
					@include rem(padding-left, 17px);
				}
			}
		}

		&:hover,
		&:active,
		&.selected {
			background-color: $highlight-color__teal;
		}
	}

	&.limit-records .scenarios-row:last-of-type {
		border-bottom-left-radius: 0.3125rem;
		border-bottom-right-radius: 0.3125rem;
	}

	.scenarios-adjust-options {
		border-bottom-left-radius: 0.3125rem;
		border-bottom-right-radius: 0.3125rem;
		@include rem(height, 44px);
		padding: 0;
		overflow: hidden;

		button {
			border: none;
			background: none;
			width: 100%;
			height: 100%;
			color: $brand-teal;
			font-size: 14px;
			font-weight: 500;
			cursor: pointer;
		}
	}

	&:last-of-type {
		border-bottom-left-radius: 0.3125rem;
		border-bottom-right-radius: 0.3125rem;
	}

	.accordion-toggle {
		position: absolute;
		@include rem(right, 38px);
		color: $brand-teal;
		font-size: $b360-font-size--larger;
		text-align: center;
		transition: all 0.35s ease-in-out;

		&.open {
			transform: rotate(-180deg);
		}
	}

	.product-checkbox {
		pointer-events: none;
	}
}

.message-modal {
	/deep/ .modal-wrapper {
		margin-top: 120px;
	}

	.modal-body {
		@include rem(padding, 22px 15px);
	}

	.action-button {
		display: block;
		margin-left: auto;
		@include rem(width, 90px);

		@include phablet {
			width: 100%;
		}
	}
}
</style>
